import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import { Bell, Check, Trash2, Loader } from 'lucide-react';
import useApi from '../hooks/useApi';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const api = useApi();
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch notifications from API
  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications');
      if (response.data.success) {
        setNotifications(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const unreadCount = notifications.filter(n => n.unread).length;

  const handleMarkAsRead = async (id) => {
    try {
      await api.put('/notifications', { notificationId: id });
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, unread: false } : n
      ));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.put('/notifications', { markAllRead: true });
      setNotifications(notifications.map(n => ({ ...n, unread: false })));
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete('/notifications', { data: { notificationId: id } });
      setNotifications(notifications.filter(n => n.id !== id));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'application':
        return '📋';
      case 'message':
        return '💬';
      case 'job':
        return '💼';
      case 'update':
        return '🔄';
      case 'billing':
        return '💳';
      case 'view':
        return '👁️';
      default:
        return '🔔';
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const notifDate = new Date(timestamp);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return notifDate.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex bg-gray-50 min-h-screen font-sans">
        <div className="fixed left-0 top-0 bottom-0 z-10">
          <Sidebar />
        </div>
        <main className="flex-1 ml-0 md:ml-24 p-8 flex items-center justify-center">
          <div className="text-center">
            <Loader className="animate-spin mx-auto text-blue-600 mb-4" size={40} />
            <p className="text-gray-600">Loading notifications...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex bg-gray-50 min-h-screen font-sans">
      <div className="fixed left-0 top-0 bottom-0 z-10">
        <Sidebar />
      </div>
      
      <main className="flex-1 ml-0 md:ml-24 p-8">
        {/* Header */}
        <div className="bg-white rounded-xl p-6 shadow-md mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-black flex items-center gap-2">
                  <Bell size={24} />
                  Notifications
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
                </p>
              </div>
            </div>
            
            {unreadCount > 0 && (
              <button 
                onClick={handleMarkAllAsRead}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Check size={16} />
                Mark All as Read
              </button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {notifications.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {notifications.map((notif) => (
                <div 
                  key={notif.id}
                  className={`p-6 hover:bg-gray-50 transition-colors ${
                    notif.unread ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="text-3xl flex-shrink-0">
                      {getNotificationIcon(notif.type)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-black">{notif.title}</h3>
                            {notif.unread && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-gray-600 mt-1">{notif.message}</p>
                          <p className="text-xs text-gray-400 mt-2">{formatTime(notif.timestamp)}</p>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {notif.unread && (
                            <button
                              onClick={() => handleMarkAsRead(notif.id)}
                              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                              title="Mark as read"
                            >
                              <Check size={16} className="text-blue-600" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(notif.id)}
                            className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete notification"
                          >
                            <Trash2 size={16} className="text-red-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Bell size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No notifications</p>
              <p className="text-gray-400 text-sm mt-2">You're all caught up!</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default NotificationsPage;
